import useLocalStorage from 'use-local-storage';

import { SignedInMethod, useAuth } from '@/features/auth';

import {
  BuddingMethods,
  BuddingSensitivity,
  CameraType,
  CellSize,
  ChamberDepth,
  Stain,
  SugarUnit,
} from '../../types';

export const LOCAL_STORAGE_SETTINGS_KEY = '__SETTINGS__';

export const useProvideSettings = () => {
  const { user } = useAuth();

  const [settings, setSettings] = useLocalStorage(
    LOCAL_STORAGE_SETTINGS_KEY,
    defaultSettings,
  );

  const updateSettings = (payload: SettingsType) => {
    let convertToInteger: any = {};

    if (!isNaN(payload.defaultDilutionSample1))
      convertToInteger['defaultDilutionSample1'] = Number(payload.defaultDilutionSample1);

    if (!isNaN(payload.defaultDilutionSample2))
      convertToInteger['defaultDilutionSample2'] = Number(payload.defaultDilutionSample2);

    if (!isNaN(payload.defaultDilutionStain1))
      convertToInteger['defaultDilutionStain1'] = Number(payload.defaultDilutionStain1);

    if (!isNaN(payload.defaultDilutionStain2))
      convertToInteger['defaultDilutionStain2'] = Number(payload.defaultDilutionStain2);

    setSettings({
      ...settings,
      [user?.signedInMethod as string]: {
        ...settings[user?.signedInMethod as keyof typeof defaultSettings],
        ...payload,
        ...convertToInteger,
      },
    });
  };

  return {
    settings: settings[
      user?.signedInMethod as keyof typeof defaultSettings
    ] as SettingsType,
    setSettings: updateSettings,
    defaultSettings:
      defaultSettings[user?.signedInMethod ? user?.signedInMethod : SignedInMethod.Wine],
  };
};

const baseDefaultSettings = {
  squareSize: 0,
  isDemoMode: false,
  imagePerAnalysis: 5,
  elongatedCell: false,
  lactobacillusDetection: false,
  detectedSquareSize: 0,
  defaultDilutionStain1: 1,
  defaultDilutionStain2: 1,
  defaultDilutionSample1: 1,
  defaultDilutionSample2: 0,
  cellSize: CellSize.Small,
  sugarUnit: SugarUnit.Brix,
  camera: CameraType.Oculyze,
  stain: Stain.MethyleneViolet,
  chamberHeight: ChamberDepth.O_2,
  ethanolBuddingMethod: BuddingMethods.ALL_CELLS,
  buddingSensitivity: BuddingSensitivity.LOW,
};

const defaultSettings = {
  [SignedInMethod.Wine]: { ...baseDefaultSettings },
  [SignedInMethod.Beer]: { ...baseDefaultSettings, cellSize: CellSize.Medium },
  [SignedInMethod.Ethanol]: {
    ...baseDefaultSettings,
    cellSize: CellSize.Medium,
    buddingSensitivity: BuddingSensitivity.LOW,
  },
};

export type SettingsType = {
  squareSize?: number;
  detectedSquareSize?: number;
  chamberHeight?: ChamberDepth;
  isDemoMode: boolean;
  imagePerAnalysis: number;
  elongatedCell: boolean;
  lactobacillusDetection: boolean;
  defaultDilutionStain1: number;
  defaultDilutionStain2: number;
  defaultDilutionSample1: number;
  defaultDilutionSample2: number;
  cellSize: CellSize;
  sugarUnit: SugarUnit;
  camera: CameraType;
  stain: Stain;
  ethanolBuddingMethod: BuddingMethods;
  buddingSensitivity: BuddingSensitivity;
};
